import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload } from '@fortawesome/free-solid-svg-icons';
import { importNuraCsv } from '../../api/backend';
import { Application } from '../../core/app';
import { escapeHtml } from '../../utils/sanitize';
import './NuraImport.css';

/**
 * User-facing CSV import page for Nura Baby.
 *
 * The import runs server-side at POST /api/nura/import using the current login
 * session. The server joins the session email to the Nura app's Firebase
 * account (Admin SDK), so rows land under the same account the app uses.
 * Children are matched by name only (each row's "Profile Name" against
 * familyz/{F}/childz/{C}/name), and records are written to the app's instreamz
 * shape.
 */
export default function NuraImport() {
    const [file, setFile] = useState(null);
    const [familyKey, setFamilyKey] = useState('');
    const [importing, setImporting] = useState(false);
    const [importError, setImportError] = useState('');
    const [result, setResult] = useState(null);

    const runImport = async () => {
        if (!file) {
            setImportError('Choose a CSV file.');
            return;
        }
        if (!Application.getAccessToken()) {
            setImportError('Sign in first.');
            return;
        }

        const form = new FormData();
        form.append('csv', file);
        if (familyKey.trim()) form.append('familyKey', familyKey.trim());

        setImporting(true);
        setImportError('');
        setResult(null);
        try {
            const r = await importNuraCsv(form);
            setResult(r.data);
        } catch (e) {
            const msg = e.response?.data?.error?.message || e.message;
            setImportError('Import failed: ' + msg);
        } finally {
            setImporting(false);
        }
    };

    return (
        <div className='app-background'>
            <div className='main-item-container nura-import-page'>
                <div className='nura-import-header'>
                    <h2>Nura Baby — Import CSV</h2>
                    <div className='nura-import-subtitle'>
                        Upload the app's export CSV. Children are matched by name only,
                        and records are written to the live database.
                    </div>
                </div>

                <div className='nura-import-section'>
                    <div className='nura-import-section-title'>
                        <FontAwesomeIcon icon={faUpload} /> Upload CSV
                    </div>
                    <label className='nura-import-field'>
                        <span>CSV file</span>
                        <input type='file' accept='.csv,text/csv' onChange={(e) => setFile(e.target.files[0] || null)} />
                    </label>
                    <label className='nura-import-field'>
                        <span>Family key (optional — leave blank to search your families by name)</span>
                        <input
                            type='text'
                            value={familyKey}
                            onChange={(e) => setFamilyKey(e.target.value)}
                            placeholder='-P3gD1V3AAobz_St0tQv'
                        />
                    </label>
                    <button
                        type='button'
                        className='nura-import-btn'
                        onClick={runImport}
                        disabled={importing || !file}
                    >
                        {importing ? 'Importing…' : 'Import'}
                    </button>
                    {importError && <div className='nura-import-status bad'>{escapeHtml(importError)}</div>}
                </div>

                <div className='nura-import-section'>
                    <div className='nura-import-section-title'>Result</div>
                    {!result && <div className='nura-import-status'>Nothing imported yet.</div>}
                    {result && (
                        <>
                            <div className='nura-import-summary'>
                                <span><strong>Imported:</strong> {result.imported ?? 0}</span>
                                <span><strong>Skipped:</strong> {(result.skipped || []).length}</span>
                            </div>
                            {result.userKey && (
                                <div className='nura-import-status ok'>
                                    Imported under {escapeHtml(result.email || 'account')} —
                                    {result.joined ? ' joined to the Nura app account' : ' same session account'} ({escapeHtml(result.userKey)})
                                </div>
                            )}
                            {(result.skipped || []).length > 0 && (
                                <ul className='nura-import-skipped'>
                                    {result.skipped.map((s, i) => (
                                        <li key={i}>
                                            {escapeHtml(s.name || '')} — {escapeHtml(s.reason || '')}
                                        </li>
                                    ))}
                                </ul>
                            )}
                            <pre className='nura-import-json'>{JSON.stringify(result, null, 2)}</pre>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
